import React, { useState } from "react";
import { Phone, MessageCircle, Mail, Clock, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { contact, contactReasons } from "../mock";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", reason: contactReasons[0], message: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) { toast.error("Please enter your name and phone number"); return; }
    toast.success("Thanks! Our team will reach out to you shortly on WhatsApp.");
    setForm({ name: "", email: "", phone: "", reason: contactReasons[0], message: "" });
  };

  const cards = [
    { icon: Phone, label: "Call Us", value: contact.phone, href: `tel:${contact.phoneRaw}` },
    { icon: MessageCircle, label: "WhatsApp", value: contact.phone, href: contact.whatsapp },
    { icon: Mail, label: "Email Us", value: contact.email, href: `mailto:${contact.email}` },
    { icon: Clock, label: "Working Hours", value: contact.hours, href: null },
  ];

  return (
    <>
      <Seo
        title="Contact ShipMeds | Medicine Courier Support 24x7 | Call & WhatsApp"
        description="Contact ShipMeds for international medicine courier from India. Talk to our experts 24x7 on Call, WhatsApp and Email for quotes, pickup and documentation help."
        path="/contact"
      />
      <PageHero title="Contact Us" subtitle="Talk to our medicine courier experts — available 24x7 on Call, WhatsApp and Email." crumb="Contact" />
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2 space-y-4">
            {cards.map((c) => (
              <a key={c.label} href={c.href || undefined} target={c.href && c.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className={`flex items-center gap-4 bg-slate-50 rounded-2xl p-5 border border-slate-100 ${c.href ? "hover:border-teal-300 hover:bg-teal-50/40" : ""} transition-colors`}>
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-blue-600 text-white flex items-center justify-center shrink-0"><c.icon className="w-6 h-6" /></div>
                <div>
                  <div className="text-xs font-bold text-slate-400 uppercase tracking-wide">{c.label}</div>
                  <div className="font-semibold text-slate-800">{c.value}</div>
                </div>
              </a>
            ))}
            <div className="flex items-center gap-4 bg-slate-50 rounded-2xl p-5 border border-slate-100">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-blue-600 text-white flex items-center justify-center shrink-0"><MapPin className="w-6 h-6" /></div>
              <div>
                <div className="text-xs font-bold text-slate-400 uppercase tracking-wide">Office</div>
                <div className="font-semibold text-slate-800">New Delhi, India</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl border border-slate-100 card-shadow p-6 sm:p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-1">Send Us a Message</h2>
              <p className="text-slate-500 mb-6">Fill in the form and we'll get back within minutes.</p>
              <form onSubmit={submit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1.5">Full Name *</label>
                    <input value={form.name} onChange={set("name")} className="w-full h-11 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" placeholder="Your name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1.5">Phone *</label>
                    <input value={form.phone} onChange={set("phone")} className="w-full h-11 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" placeholder="+91 ..." />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1.5">Email</label>
                    <input value={form.email} onChange={set("email")} className="w-full h-11 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" placeholder="you@example.com" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1.5">How can we help?</label>
                    <select value={form.reason} onChange={set("reason")} className="w-full h-11 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500">
                      {contactReasons.map((r) => <option key={r}>{r}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1.5">Message</label>
                  <textarea value={form.message} onChange={set("message")} rows={4} className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" placeholder="Tell us about the medicines and destination country..." />
                </div>
                <button type="submit" className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-bold px-7 py-3.5 rounded-lg transition-colors">
                  <Send className="w-4 h-4" /> Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
